import { PasswordInput, TextInput } from "@/components/shared/FormInputs";
import { Shield } from "lucide-react";
import React, { useState } from "react";

interface ProfileTabViewProps {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  isSaving: boolean;
  isPasswordTooShort: boolean;
  doesPasswordMismatch: boolean;
  setName: (name: string) => void;
  setPassword: (password: string) => void;
  setConfirmPassword: (password: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onReset: () => void;
}

export const ProfileTabView: React.FC<ProfileTabViewProps> = ({
  name,
  email,
  password,
  confirmPassword,
  isSaving,
  isPasswordTooShort,
  doesPasswordMismatch,
  setName,
  setPassword,
  setConfirmPassword,
  onSubmit,
  onReset,
}) => {
  const [isPasswordTouched, setIsPasswordTouched] = useState(false);
  const [isConfirmPasswordTouched, setIsConfirmPasswordTouched] = useState(false);

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-1">
      <p className="-mt-2 mb-3 text-sm text-muted-foreground">
        Manage your account profile and security preferences.
      </p>

      <TextInput
        id="settings-full-name"
        label="Display Name"
        value={name}
        onChange={setName}
        placeholder="Enter your display name"
        required
        disabled={isSaving}
        autoComplete="name"
      />

      <div className="-mt-3">
        <TextInput
          id="settings-email-address"
          label="Primary Email"
          type="email"
          value={email}
          placeholder="Enter your email"
          onChange={() => undefined}
          disabled
          autoComplete="email"
        />
        <p className="-mt-4 mb-5 text-xs text-muted-foreground">
          Your email is managed by your organization.
        </p>
      </div>

      <div className="mb-4 h-px w-full bg-border" />

      <div className="mb-5">
        <span className="flex items-center gap-2 text-sm font-bold text-primary">
          <Shield size={16} strokeWidth={2.5} />
          Secure Password Update
        </span>
        <p className="mb-3 mt-1 text-xs text-muted-foreground">
          Leave both fields blank to keep your current password.
        </p>

        <div className="flex flex-col">
          <PasswordInput
            id="settings-new-password"
            label="New Password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            disabled={isSaving}
            error={
              isPasswordTouched && isPasswordTooShort
                ? "Password must be at least 6 characters."
                : undefined
            }
            onBlur={() => setIsPasswordTouched(true)}
            wrapperClassName="mb-1"
          />

          <PasswordInput
            id="settings-confirm-password"
            label="Confirm Password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            disabled={isSaving}
            error={
              isConfirmPasswordTouched && doesPasswordMismatch
                ? "Passwords do not match."
                : undefined
            }
            onBlur={() => setIsConfirmPasswordTouched(true)}
            wrapperClassName="mb-0"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => {
            setIsPasswordTouched(false);
            setIsConfirmPasswordTouched(false);
            onReset();
          }}
          disabled={isSaving}
          className="flex-1 rounded-2xl border border-input bg-card py-3 text-sm font-bold text-muted-foreground transition-colors hover:bg-surface-subtle disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="flex-1 rounded-2xl bg-primary py-3 text-sm font-bold text-white shadow-md transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};
